import type { TfClient } from '../client-internal';
import type { Database } from '../types.gen';

export type RevenueEvent = Database['public']['Tables']['tf_revenue_events']['Row'];
export type RevenueEventInsert = Database['public']['Tables']['tf_revenue_events']['Insert'];
export type RevenueSource = Database['public']['Enums']['tf_revenue_source'];

export interface RevenueFilter {
  toolId?: string;
  source?: RevenueSource;
  since?: string;
  until?: string;
  limit?: number;
}

export interface RevenueTotals {
  total_cents: number;
  by_source: Record<RevenueSource, number>;
}

export async function insertRevenueEvent(
  c: TfClient,
  input: RevenueEventInsert,
): Promise<RevenueEvent> {
  const { data, error } = await c.from('tf_revenue_events').insert(input).select('*').single();
  if (error) throw error;
  return data;
}

export async function listRevenueEvents(
  c: TfClient,
  filter: RevenueFilter = {},
): Promise<RevenueEvent[]> {
  let q = c
    .from('tf_revenue_events')
    .select('*')
    .order('occurred_at', { ascending: false })
    .limit(filter.limit ?? 500);
  if (filter.toolId) q = q.eq('tool_id', filter.toolId);
  if (filter.source) q = q.eq('source', filter.source);
  if (filter.since) q = q.gte('occurred_at', filter.since);
  if (filter.until) q = q.lte('occurred_at', filter.until);
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

const ALL_SOURCES: RevenueSource[] = [
  'lead_sale',
  'affiliate',
  'subscription',
  'one_time',
  'house_route',
];

export async function sumRevenueByTool(
  c: TfClient,
  toolId: string,
  range?: { since?: string; until?: string },
): Promise<RevenueTotals> {
  let q = c.from('tf_revenue_events').select('source, amount_cents').eq('tool_id', toolId);
  if (range?.since) q = q.gte('occurred_at', range.since);
  if (range?.until) q = q.lte('occurred_at', range.until);
  const { data, error } = await q;
  if (error) throw error;
  const by_source = Object.fromEntries(ALL_SOURCES.map((s) => [s, 0])) as Record<
    RevenueSource,
    number
  >;
  let total = 0;
  for (const row of data ?? []) {
    const cents = Number(row.amount_cents);
    by_source[row.source] += cents;
    total += cents;
  }
  return { total_cents: total, by_source };
}

export interface RevenueSeriesPoint {
  day: string; // YYYY-MM-DD
  total_cents: number;
  by_source: Record<RevenueSource, number>;
}

/**
 * Bucket revenue by UTC day. v1 grouping happens client-side after pulling rows;
 * upgrade to a SQL view or materialized view when total events exceed ~10k.
 */
export async function revenueDailySeries(
  c: TfClient,
  toolId: string | null,
  range: { since: string; until: string },
): Promise<RevenueSeriesPoint[]> {
  let q = c
    .from('tf_revenue_events')
    .select('occurred_at, source, amount_cents')
    .gte('occurred_at', range.since)
    .lte('occurred_at', range.until)
    .order('occurred_at', { ascending: true });
  if (toolId) q = q.eq('tool_id', toolId);
  const { data, error } = await q;
  if (error) throw error;

  const buckets = new Map<string, RevenueSeriesPoint>();
  for (const row of data ?? []) {
    const day = row.occurred_at.slice(0, 10);
    let p = buckets.get(day);
    if (!p) {
      p = {
        day,
        total_cents: 0,
        by_source: Object.fromEntries(ALL_SOURCES.map((s) => [s, 0])) as Record<
          RevenueSource,
          number
        >,
      };
      buckets.set(day, p);
    }
    const cents = Number(row.amount_cents);
    p.total_cents += cents;
    p.by_source[row.source] += cents;
  }
  return Array.from(buckets.values());
}
